<template>
    <ValidationObserver tag="fieldset" ref="payDatesObserver">
        <expand-transition group>
            <digi-select
                label="When do you next get paid?"
                name="next_pay_date"
                ref="next_pay_date"
                :rules="this.$getValidationRules('next_pay_date')"
                :custom-messages="this.$getValidationMessages('next_pay_date')"
                :options="nextPayDateOptions"
                v-model="localNextPayDate"
                v-show="shouldShowNextPayDate"
                class="mb-8"
            ></digi-select>
        </expand-transition>

        <expand-transition group>
            <digi-select
                label="Following pay date"
                name="following_pay_date"
                ref="following_pay_date"
                :rules="this.$getValidationRules('following_pay_date')"
                :custom-messages="this.$getValidationMessages('following_pay_date')"
                :options="filteredFollowingPayDateOptions"
                v-model="localFollowingPayDate"
                v-show="shouldShowFollowingPayDate"
                class="mb-8"
            ></digi-select>
        </expand-transition>
    </ValidationObserver>
</template>

<script>
import DigiSelect from "./digi-select";
import { getLastPossibleDateFromOptions, getLastXDateOfMonth } from '../../assets/js/pay-dates';
import ExpandTransition from "./../../components/expand-transition";
import { has, filter, object, omit } from "underscore";
import { isEmpty } from "underscore";

const moment = require("moment");

export default {
    components: {
        "digi-select": DigiSelect,
        "expand-transition": ExpandTransition,
    },
    inject: ["$getValidationRules", "$getValidationMessages", '$globals', '$customer'],
    props: {
        form: {},
        nextPayDate: {},
        followingPayDate: {},
        nextPayDateOptions: {},
        followingPayDateOptions: {},
        name: {},
        forceShowNextPayDate: {
            default: true,
        },
        forceShowFollowingPayDate: {
            default: true,
        },
    },
    data() {
        return {
            thisForceShowNextPayDate: this.forceShowNextPayDate,
            thisForceShowFollowingPayDate: this.forceShowFollowingPayDate,
            localNextPayDate: this.nextPayDate,
            localFollowingPayDate: this.followingPayDate,
        };
    },
    computed: {
        shouldShowNextPayDate() {
            if (!isEmpty(this.$customer.thirdparty_prepop)) {
                return true;
            }
            return (
                this.thisForceShowNextPayDate ||
                (this.form.customer.pay_frequency &&
                    [
                        "specific_day",
                        "four_weekly",
                        "twice_monthly",
                        "bi_weekly",
                        "weekly",
                    ].includes(this.form.customer.pay_frequency))
            );
        },
        shouldShowFollowingPayDate() {
            if (!isEmpty(this.$customer.thirdparty_prepop)) {
                return true;
            }
            return (
                this.thisForceShowFollowingPayDate ||
                (this.form.customer.pay_frequency &&
                    [
                        "four_weekly",
                        "twice_monthly",
                        "bi_weekly",
                        "weekly",
                    ].includes(this.form.customer.pay_frequency))
            );
        },
        filteredFollowingPayDateOptions() {
            if (
                this.localNextPayDate &&
                has(this.followingPayDateOptions, this.localNextPayDate)
            ) {
                let nextPayDateMomentPlusFive = moment(
                    this.localNextPayDate
                ).add({ days: 5 });
                return omit(this.followingPayDateOptions, function (
                    value,
                    key
                ) {
                    if (key == "") {
                        return false;
                    }
                    return moment(key) <= nextPayDateMomentPlusFive;
                });
            } else {
                return this.followingPayDateOptions;
            }
        },
    },
    watch: {
        localNextPayDate: {
            handler(newValue, oldValue) {
                this.$emit("update:next-pay-date", newValue);
            },
        },
        localFollowingPayDate: {
            handler(newValue, oldValue) {
                this.$emit("update:following-pay-date", newValue);
            },
        },
        "form.customer.pay_frequency": {
            handler: function (newValue, oldValue) {
                if (newValue !== oldValue) {
                    this.setPayDates();
                }
            },
            deep: true,
            immediate: true,
        },
        nextPayDate: {
            handler: function (newValue, oldValue) {
                this.setFollowingPayDateBasedOnFrequencyAndNextPayDate(newValue);
            },
            deep: true,
        },
    },
    methods: {
        setFollowingPayDateBasedOnFrequencyAndNextPayDate(nextPayDate)
        {
            let folllowingMap = {
                    four_weekly: { weeks: 4 },
                    bi_weekly: { weeks: 2 },
                    weekly: { weeks: 1 },
                    specific_day: { months: 1 },
                };
                if (
                    Object.keys(folllowingMap).includes(
                        this.form.customer.pay_frequency
                    ) &&
                    moment(nextPayDate).isValid()
                ) {
                    // Handle the following pay date, not needed for any other
                    let targetDate = moment(nextPayDate)
                        .clone()
                        .add(folllowingMap[this.form.customer.pay_frequency]);
                    // Try and set it                    
                    targetDate = getLastPossibleDateFromOptions(
                        targetDate,
                        this.filteredFollowingPayDateOptions
                    );
                    this.localFollowingPayDate = targetDate.format(
                        "YYYY-MM-DD"
                    );
                    try {
                        this.$refs.following_pay_date.$refs.provider.validate();
                    } catch (e) {}
                }
        },
        setPayDates() {
            let nextPayDateOptions = this.nextPayDateOptions;
            let followingPayDateOptions = this.filteredFollowingPayDateOptions;
            let nextPayDate = null;
            let followingPayDate = null;
            let dayMap = {
                last_working_day: null,
                last_monday: 1,
                last_tuesday: 2,
                last_wednesday: 3,
                last_thursday: 4,
                last_friday: 5,
            };
            switch (this.form.customer.pay_frequency) {
                case "last_working_day":
                case "last_monday":
                case "last_tuesday":
                case "last_wednesday":
                case "last_thursday":
                case "last_friday":
                    nextPayDate = getLastXDateOfMonth(
                        dayMap[this.form.customer.pay_frequency]
                    );
                    break;
                default:
                    return;
            }
            if (nextPayDate) {
                nextPayDate = getLastPossibleDateFromOptions(
                    nextPayDate,
                    nextPayDateOptions
                );
                this.localNextPayDate = nextPayDate.format("YYYY-MM-DD");
                followingPayDate = getLastPossibleDateFromOptions(
                    getLastXDateOfMonth(
                        dayMap[this.form.customer.pay_frequency],
                        nextPayDate.add(1, "months")
                    ),
                    followingPayDateOptions
                );
                this.localFollowingPayDate = followingPayDate.format(
                    "YYYY-MM-DD"
                );
            } else {
                return;
            }
        },
        decideWhetherToShowFieldsOnLoad() {
            if (this.form.form.journey_id === 4) {
                console.log("On remarketing journey");
                this.$refs.payDatesObserver.validate().then((result) => {
                    console.log(result);
                    if (!result) {
                        this.thisForceShowNextPayDate = true;
                        this.thisForceShowFollowingPayDate = true;
                        this.$refs.payDatesObserver.reset();
                    }
                });
            }
        },
    },
    mounted() {
        this.decideWhetherToShowFieldsOnLoad();
        if (!!this.$customer.next_pay_date && !!this.$customer.pay_frequency && this.$customer.following_pay_date == null) {
            this.setFollowingPayDateBasedOnFrequencyAndNextPayDate(this.$customer.next_pay_date);
        }
    },
};
</script>

<style lang="scss">
</style>